import styled, { keyframes } from "styled-components";

// ─── Animations ─────────────────────────────────────────────────────────

const inputFocusPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--color-brand-400-rgb), 0); }
  50%      { box-shadow: 0 0 0 4px rgba(var(--color-brand-400-rgb), 0.18); }
`;

// ─── Shell ──────────────────────────────────────────────────────────────

export const ModalContainer = styled.div`
  position: relative;
  width: min(900px, calc(100vw - 32px));
  max-width: 92vw;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      130% 70% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  /* Lower blur (was 28px). bg above is 0.94-0.96 alpha. panelFloat
     brightness keyframe also dropped — ran continuously while modal
     was open, forcing a full raster pass per frame. */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.38),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 18px rgba(var(--color-brand-200-rgb), 0.24);
  overflow: visible;

  [data-theme="dark"] & {
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -10px 18px rgba(var(--color-brand-700-rgb), 0.18);
    &::after {
      opacity: 0.35;
    }
  }

  /* Top specular crescent */
  &::before {
    content: "";
    position: absolute;
    top: 4px;
    left: 28px;
    right: 28px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Outer halo */
  &::after {
    content: "";
    position: absolute;
    inset: -12px;
    border-radius: inherit;
    background:
      radial-gradient(
        65% 55% at 20% 25%,
        rgba(var(--color-brand-300-rgb), 0.32) 0%,
        transparent 70%
      ),
      radial-gradient(
        60% 50% at 80% 80%,
        rgba(var(--color-brand-400-rgb), 0.22) 0%,
        transparent 70%
      );
    filter: blur(18px);
    z-index: -1;
    pointer-events: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: unset;
    border-radius: 24px;
  }

  @media (max-width: 768px) {
    width: 380px;
    max-width: 92vw;
    display: block;
  }
`;

// ─── Header buttons ──────────────────────────────────────────────────────

const headerButton = `
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.22);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78),
    rgba(var(--glass-white-rgb), 0.5),
    rgba(var(--glass-pale-rgb), 0.38)
  );
  color: var(--color-text-strong, var(--color-text-strong));
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  &:hover {
    transform: translateY(-1px);

    box-shadow:
      0 10px 22px rgba(var(--color-brand-500-rgb), 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
  }

  [data-theme="dark"] &:hover {
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.55),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.2);
  }

  svg {
    width: 14px;
    height: 14px;
  }

  /* Many of our shared icons hardcode stroke="white" — flip to brand text. */
  svg path {
    stroke: var(--color-text-strong, var(--color-text-strong));
  }
`;

export const CloseButton = styled.button`
  ${headerButton};
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 10;
`;

export const ChevronButton = styled.button`
  ${headerButton};
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 2;
`;

// ─── Body ────────────────────────────────────────────────────────────────

export const ModalContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 36px 26px 26px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  min-width: 0;
`;

// ─── Desktop visual panel ────────────────────────────────────────────────

export const AuthShowcase = styled.aside`
  position: relative;
  min-width: 0;
  min-height: 590px;
  overflow: hidden;
  isolation: isolate;
  border-radius: 0 27px 27px 0;
  background: var(--color-brand-700);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    background:
      linear-gradient(
        180deg,
        rgba(10, 39, 71, 0.04) 18%,
        rgba(10, 39, 71, 0.2) 50%,
        rgba(7, 29, 54, 0.88) 100%
      ),
      linear-gradient(90deg, rgba(15, 72, 123, 0.12), transparent 42%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    border-radius: inherit;
    box-shadow:
      inset 1px 0 0 rgba(255, 255, 255, 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.42);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const AuthShowcaseImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: 50% 44%;
`;

export const AuthBeforeCard = styled.div`
  position: absolute;
  top: 22px;
  left: 22px;
  z-index: 3;
  width: 82px;
  height: 104px;
  overflow: hidden;
  border: 2px solid rgba(247, 252, 255, 0.92);
  border-radius: 14px;
  background: rgba(247, 252, 255, 0.18);
  transform: rotate(-4deg);
  transform-origin: center;
  box-shadow:
    0 14px 30px rgba(5, 25, 46, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
`;

export const AuthBeforeImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: 50% 28%;
`;

export const AuthShowcaseContent = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 30px 32px;
  color: rgba(247, 252, 255, 0.98);
`;

export const AuthBenefitList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  width: 100%;
`;

export const AuthBenefit = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: rgba(247, 252, 255, 0.95);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  text-shadow: 0 2px 9px rgba(5, 25, 46, 0.4);
`;

export const AuthBenefitIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
  background: rgba(118, 201, 255, 0.24);
  color: rgba(247, 252, 255, 0.98);
  font-size: 12px;
  font-weight: 800;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 4px 12px rgba(5, 25, 46, 0.16);
`;

export const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 2px;

  img {
    height: 48px;
    width: auto;
    display: block;
    pointer-events: none;
    filter: drop-shadow(0 4px 8px rgba(var(--color-brand-400-rgb), 0.28))
      drop-shadow(0 1px 2px rgba(var(--glass-white-rgb), 0.5));
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  text-align: center;
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const ModalSubtitle = styled.span`
  color: var(--color-text-body, #34547a);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.005em;
  text-align: center;
  line-height: normal;
`;

// ─── Social column ───────────────────────────────────────────────────────

export const SocialColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const LoginIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-right: 6px;
`;

// ─── GoogleButton — UNCHANGED per user request ───────────────────────────

export const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding-right: 13.2px;
  height: 42px;
  border-radius: 100px;
  border: 1px solid #dadce0;
  background: #fff;
  color: #3c4043;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.21px;
  cursor: pointer;
  outline: none;
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: #f8f9fa;
    box-shadow: 0 1px 3px rgba(60, 64, 67, 0.3);
  }

  &:active {
    background: #f1f3f4;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }
`;

// ─── PrimaryActionButton — Light Water CTA ───────────────────────────────

export const PrimaryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 46px;
  padding: 0 20px;
  border-radius: 100px;
  border: 1px solid var(--glass-edge-faint);
  background: linear-gradient(
    180deg,
    var(--color-brand-400) 0%,
    var(--color-brand-400) 50%,
    var(--color-brand-700) 100%
  );
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  position: relative;
  overflow: hidden;
  box-shadow:
    0 10px 24px rgba(var(--color-brand-600-rgb), 0.34),
    0 4px 10px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -2px 6px rgba(0, 0, 0, 0.06);

  [data-theme="dark"] & {
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.5),
      0 3px 8px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.18),
      inset 0 -2px 6px rgba(0, 0, 0, 0.2);
  }
  transition:
    transform 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy),
    opacity 180ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      transparent 30%,
      rgba(var(--glass-white-rgb), 0.36) 50%,
      transparent 70%
    );
    transform: translateX(-120%);
    transition: transform 600ms var(--ease-snappy);
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 14px 30px rgba(var(--color-brand-600-rgb), 0.4),
      0 6px 14px rgba(var(--color-brand-400-rgb), 0.26),
      inset 0 1px 0 rgba(255, 255, 255, 0.55),
      inset 0 -2px 6px rgba(0, 0, 0, 0.06);
    filter: brightness(1.06);
  }

  &:hover:not(:disabled)::before {
    transform: translateX(120%);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow:
      0 6px 16px rgba(var(--color-brand-600-rgb), 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 -2px 6px rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: saturate(0.65);
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--glass-text-on-pill);
  }
`;

// ─── Divider ─────────────────────────────────────────────────────────────

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin: 4px 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(var(--color-brand-300-rgb), 0) 0%,
      rgba(var(--color-brand-300-rgb), 0.32) 50%,
      rgba(var(--color-brand-300-rgb), 0) 100%
    );
  }
`;

export const DividerText = styled.span`
  color: var(--color-text-muted, #6b88a8);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

// ─── Email section ───────────────────────────────────────────────────────

export const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

export const EmailInput = styled.input`
  width: 100%;
  height: 46px;
  padding: 0 16px;
  border-radius: 14px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.32);
  background: var(--glass-base-2);
  color: var(--color-text-strong, var(--color-text-strong));
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.005em;
  outline: none;
  box-shadow:
    inset 0 2px 4px rgba(var(--color-brand-500-rgb), 0.06),
    0 1px 0 rgba(var(--glass-white-rgb), 0.95);
  transition:
    border-color 200ms var(--ease-snappy),
    background 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  &::placeholder {
    color: var(--color-text-muted, #6b88a8);
    font-weight: 500;
  }

  &:focus {
    border-color: rgba(var(--color-brand-400-rgb), 0.7);
    background: var(--glass-base-1);
    box-shadow:
      0 0 0 4px rgba(var(--color-brand-400-rgb), 0.15),
      inset 0 2px 4px rgba(var(--color-brand-500-rgb), 0.04);
  }
`;

export const TurnstileWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
`;

// ─── Verification (code input) view ──────────────────────────────────────

export const VerificationMessage = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  gap: 18px;
`;

export const CodeInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: nowrap;
  width: 100%;
`;

export const CodeInput = styled.input`
  flex: 1 1 0;
  min-width: 0;
  max-width: 48px;
  height: 56px;
  padding: 0;
  border-radius: 14px;
  border: 1.5px solid rgba(var(--color-brand-300-rgb), 0.32);
  background: var(--glass-base-1);
  color: var(--color-text-strong, var(--color-text-strong));
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  font-family: var(--font-family-primary);
  caret-color: var(--color-brand-500, var(--color-brand-400));
  outline: none;
  box-shadow:
    inset 0 2px 4px rgba(var(--color-brand-500-rgb), 0.08),
    0 1px 0 rgba(var(--glass-white-rgb), 0.95);
  transition:
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy),
    transform 180ms var(--ease-snappy);

  &:focus {
    border-color: var(--color-brand-500, var(--color-brand-400));
    background: rgba(var(--glass-white-rgb), 1);
    transform: translateY(-1px);
    box-shadow:
      0 0 0 4px rgba(var(--color-brand-400-rgb), 0.18),
      inset 0 2px 4px rgba(var(--color-brand-500-rgb), 0.04);
    animation: ${inputFocusPulse} 1.6s ease-in-out;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  @media (max-width: 480px) {
    height: 52px;
    font-size: 20px;
  }
`;

export const ResendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--color-text-muted, #6b88a8);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
`;

export const ResendButton = styled.span`
  color: var(--color-brand-600, var(--color-brand-500));
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  padding-bottom: 1px;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1.5px;
    background: linear-gradient(
      90deg,
      var(--color-brand-500, var(--color-brand-400)),
      var(--color-brand-700, var(--color-brand-700))
    );
    border-radius: 999px;
    transition: opacity 160ms var(--ease-snappy);
  }

  &:hover {
    color: var(--color-brand-700, var(--color-brand-700));
  }
`;

export const ResendCountdown = styled.span`
  color: var(--color-text-muted, #6b88a8);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  cursor: default;
`;

export const EmailPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: linear-gradient(
    180deg,
    rgba(var(--glass-white-rgb), 0.95) 0%,
    rgba(var(--glass-pale-rgb), 0.85) 100%
  );
  color: var(--color-text-strong, var(--color-text-strong));
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
  box-shadow:
    0 4px 10px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);

  svg {
    color: var(--color-brand-600, var(--color-brand-500));
    cursor: pointer;
    transition:
      color 160ms var(--ease-snappy),
      transform 160ms var(--ease-snappy);

    &:hover {
      color: var(--color-brand-700, var(--color-brand-700));
      transform: rotate(-8deg) scale(1.08);
    }
  }
`;

// Legacy aliases (kept so other consumers don't break if any)
export const LabelContainer = EmailSection;
export const Label = DividerText;
export const TitleContainerLegacy = TitleContainer;
